// The catalog view (spec §13.3): every Google family inside the font sheet. picker.js owns the dialog, the language chip
// and the switch between views; this module owns #catalog-view. Rows preview the family name (+ the F7 CJK sample) through
// css2 at the family's default weight — never the visitor's text — and carry the "not yet print-tested" mark unless the
// row IS a curated entry (ruling R95: by family and weight, a bundled family too — picker-model.js catalogRow).
// Nothing of the core is imported (ruling R87): `core` is handed in.
import { loadCatalog } from './catalog.js';
import { loadGooglePreview } from './gfont.js';
import { CATEGORIES, catalogMatches, catalogRow } from './picker-model.js';
import { esc } from './esc.js';

const PAGE = 50;

export function createCatalogView({ root, S, curated, core, onPick }) {
  const search = root.querySelector('#cat-search'), tabs = root.querySelector('#cat-tabs'), rows = root.querySelector('#cat-rows');
  root.querySelector('#cat-back').textContent = S.backToCurated;
  search.placeholder = S.searchFonts; search.setAttribute('aria-label', S.searchFonts);
  tabs.innerHTML = CATEGORIES.map((c) => `<button type="button" data-v="${c}">${esc(S.category[c])}</button>`).join('');
  let list = null, ctx = null, cat = 'all', q = '', matches = [], shown = 0;
  const paintTabs = () => tabs.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset.v === cat));
  tabs.addEventListener('click', (e) => { const b = e.target.closest('button'); if (!b) return; cat = b.dataset.v; paintTabs(); refilter(); });
  search.addEventListener('input', () => { q = search.value; refilter(); });

  const previewOf = (f, r) => `${f.family} ${S.sample[r.cjk] || ''}`.trim();
  // One observer for both jobs: a row scrolled into view loads its preview; the sentinel row pages in the next 50.
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (!en.isIntersecting) return;
    io.unobserve(en.target);
    if (en.target.id === 'cat-more') { en.target.remove(); page(); return; }
    const f = matches[+en.target.dataset.i], r = catalogRow(f, { curated, core });
    loadGooglePreview(f.family, r.weight, previewOf(f, r));
  }));

  // the row the design's font is on: its family, whichever weight (a bundled entry by its family name)
  const currentFamily = () => (ctx.current ? ((core.curatedEntry(ctx.current, curated) || {}).family || ctx.current.id) : null);
  function rowHtml(f, i) {
    const r = catalogRow(f, { curated, core }), bad = ctx.failed.has(core.fontKey(r.match || r.pick));
    const meta = [esc(f.family)];
    if (r.cjk) meta.push(`<span class="badge">${esc(S.scriptName[r.cjk])}</span>`);
    if (r.bytes) meta.push(esc(S.mb(r.bytes / 1048576)));
    if (!r.match) meta.push(`<span class="badge">${esc(S.untested)}</span>`);
    if (bad) meta.push(`<span class="badge fail">${esc(S.fontLoadFailed)}</span>`);
    const on = currentFamily() === f.family;
    return `<li><button type="button" class="font-row cat-row${on ? ' on' : ''}${bad ? ' failed' : ''}" data-i="${i}" data-id="${esc(f.family)}">
      <span class="pv">${esc(previewOf(f, r))}</span><span class="fam">${meta.join(' · ')}</span></button></li>`;
  }
  function page() {
    const end = Math.min(shown + PAGE, matches.length);
    rows.insertAdjacentHTML('beforeend', matches.slice(shown, end).map((f, j) => rowHtml(f, shown + j)).join(''));
    // font-family is set as a property, never interpolated into a style string (a family name is data).
    rows.querySelectorAll('.cat-row:not([data-seen])').forEach((b) => {
      b.dataset.seen = '1';
      b.querySelector('.pv').style.fontFamily = `"${matches[+b.dataset.i].family.replace(/["\\]/g, '')}", var(--font-body)`;
      io.observe(b);
    });
    shown = end;
    if (shown < matches.length) { rows.insertAdjacentHTML('beforeend', '<li id="cat-more" class="more"></li>'); io.observe(rows.querySelector('#cat-more')); }
  }
  // the list, loaded once it is here (catalog.js); until then the loading line, and the failed line if it does not come
  function load() {
    io.disconnect();
    rows.innerHTML = `<li class="empty">${esc(S.catalogLoading)}</li>`;
    loadCatalog().then((l) => { list = l; refilter(); }).catch(() => { rows.innerHTML = `<li class="empty">${esc(S.catalogFailed)}</li>`; });
  }
  function refilter() {
    io.disconnect(); rows.innerHTML = ''; shown = 0;
    if (!list) { load(); return; }                                     // no list yet (a failed load is not kept): ask again, with its line (M5)
    matches = catalogMatches({ list, cat, q, cls: ctx.cls, filterOn: ctx.filterOn, core });
    if (!matches.length) { rows.innerHTML = `<li class="empty">${esc(S.noFontFound)}</li>`; return; }
    page();
  }
  rows.addEventListener('click', (e) => {
    const b = e.target.closest('.cat-row'); if (!b) return;
    // a curated family at its curated weight picks as the curated entry (the page's own rules for it apply — name-tag's
    // smallest height); otherwise a plain catalog pick
    onPick(catalogRow(matches[+b.dataset.i], { curated, core }).pick);
  });

  return {
    reset() { cat = 'all'; q = ''; search.value = ''; paintTabs(); },
    render(context) {                                                  // { cls, filterOn, current (doc font), failed (Set of fontKey) }
      ctx = context;
      if (list) { refilter(); return; }
      load();
    },
  };
}
